import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import type { FileActionContext } from '@jotta/types/FileActions'
import { plural, t } from '@lingui/macro'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useFileRouteParams } from '../FileRoute/utils/getFileRouteParams'
import type { FolderStore } from '../Folder/FolderStore'
import { FileRouteLink } from '../FileRouteLink/FileRouteLink'
import SvgListDashes from '@jotta/ui/SvgListDashes'
import SvgGrid from '@jotta/ui/SvgGrid'

const debug = Debug('jotta:files:PathItemRow')

export const FileActions = observer<{
  folder: FolderStore
}>(function FileActions({ folder }) {
  const { context } = useFileRouteParams()
  const isSearch = context === 'search'
  const {
    actions,
    selection,
    selectedActions,
    someSelected: indeterminate,
  } = folder
  const selectedFiles = folder.selectedChildren.filter(
    child => child.isFile,
  ).length
  const selectedFolders = folder.selectedChildren.filter(
    child => child.isFolder,
  ).length

  let selectionText = ''

  if (selectedFiles + selectedFolders === 1) {
    selectionText = folder.selectedChildren[0].name
  } else {
    if (selectedFolders) {
      const count = selectedFolders
      selectionText = plural(count, { one: '# folder', other: '# folders' })
    }
    if (selectedFiles && selectedFolders) {
      selectionText += ` ${t`and`} `
    }
    if (selectedFiles) {
      const count = selectedFiles
      selectionText += plural(count, { one: '# file', other: '# files' })
    }
  }

  const selectionActionContext: FileActionContext = folder.oneSelected
    ? 'FOLDER_LIST'
    : 'SELECTION'
  const selectionActionPath = folder.selectedChildren.at(0)?.path || ''

  useEffect(() => {
    selection.setIndeterminate(indeterminate)
  }, [selection, indeterminate])

  const nextViewMode = folder.route.viewMode === 'list' ? 'thumbs' : 'list'
  const viewModeIcon = nextViewMode === 'list' ? SvgListDashes : SvgGrid
  return (
    <div className="h-[56px] flex-none shrink-0 bg-primary py-1">
      {!folder.selectedChildren.length ? (
        <div className="flex h-full grow justify-end pr-[10px]">
          <FileRouteLink
            data-testid={`ViewMode${nextViewMode === 'list' ? 'List' : 'Thumbs'}Button`}
            viewMode={nextViewMode}
            replace={isSearch}
          >
            <PlainBrandIcon className="w-6" icon={viewModeIcon} />
          </FileRouteLink>
        </div>
      ) : (
        <div className="flex h-full grow items-center justify-between gap-3 rounded-md bg-informative pl-1">
          <span
            className="label-sm min-w-0 flex-1 truncate pl-3"
            title={selectionText}
          >
            {selectionText}
          </span>
          <div className="group shrink-0 px-1">
            {selectedActions.map(a => (
              <button
                className="btn btn-ghost"
                key={a.name}
                data-select-action={a.action}
                onClick={() => {
                  actions.dispatch(
                    a.action,
                    selectionActionContext,
                    selectionActionPath,
                  )
                }}
              >
                <PlainBrandIcon className="w-6" icon={a.icon} />
                <span className="hidden xl:block">{a.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
})
